import { type HTMLAttributes, type Ref } from "react";

import { cn } from "../../utils";

export const ACCOUNT_ICON_SIZES = ["xs", "sm", "md", "lg", "xl"];

export type AccountIconSize = (typeof ACCOUNT_ICON_SIZES)[number];

export const ACCOUNT_ICONS_VARIANTS = [
  "blue",
  "green",
  "orange",
  "pink",
  "purple",
] as const;

export type AccountIconVariant = (typeof ACCOUNT_ICONS_VARIANTS)[number];

const VARIANT_URLS: Record<AccountIconVariant, string> = {
  blue: new URL("../assets/accounts/blue.svg", import.meta.url).href,
  green: new URL("../assets/accounts/green.svg", import.meta.url).href,
  orange: new URL("../assets/accounts/orange.svg", import.meta.url).href,
  pink: new URL("../assets/accounts/pink.svg", import.meta.url).href,
  purple: new URL("../assets/accounts/purple.svg", import.meta.url).href,
};

const KYAN_URL = new URL("../assets/accounts/kyanlogo.svg", import.meta.url)
  .href;

export interface AccountIconProps extends HTMLAttributes<HTMLDivElement> {
  ref?: Ref<HTMLDivElement>;
  size?: AccountIconSize;
  variant: AccountIconVariant;
}

export const AccountIcon = ({
  size = "md",
  variant,
  className,
  onClick,
  ref,
  ...props
}: AccountIconProps) => {
  const src = VARIANT_URLS[variant];

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events -- The img element is used here for displaying the account icon and needs to handle click events for interaction purposes.
    <div
      ref={ref}
      className={cn(
        "ui:relative ui:flex ui:items-center ui:justify-center",
        {
          "ui:cursor-pointer": Boolean(onClick),
        },
        className,
      )}
      onClick={onClick}
      {...props}
    >
      <img
        src={src}
        alt={variant}
        className={cn("ui:rounded-full", {
          "ui:size-3": size === "xs",
          "ui:size-4": size === "sm",
          "ui:size-5": size === "md",
          "ui:size-6": size === "lg",
          "ui:size-7": size === "xl",
        })}
      />
      <img
        alt="kyan"
        src={KYAN_URL}
        className={cn("ui:absolute ui:pt-px ui:pl-px", {
          "ui:size-2": ["xs", "sm"].includes(size),
          "ui:size-2.5": size === "md",
          "ui:size-3": size === "lg",
          "ui:size-3.5": size === "xl",
        })}
      />
    </div>
  );
};
